import { useMemo } from "react";

export interface BaseScatterPoint {
  optionId?: string;
  label: string;
  imageUrl?: string;
  isHighlighted?: boolean;
  isRankedByUser?: boolean;
  xValue?: number;
  yValue?: number;
  labelOffsetY?: number;
  labelSide?: "left" | "right";
}

export interface ScatterLabelOptions<T extends BaseScatterPoint> {
  getX?: (point: T) => number;
  getY?: (point: T) => number;
  domainMax?: number;
  labelSpacing?: number;
  groupingPrecision?: number;
  alternateGroupPredicate?: (groupKey: number | string, points: T[]) => boolean;
}

interface ScatterShapeProps {
  cx?: number;
  cy?: number;
  payload?: BaseScatterPoint;
  size?: number;
}

export const SCATTER_POINT_SIZE = 28;

export const ScatterNode = ({ cx = 0, cy = 0, payload, size = SCATTER_POINT_SIZE }: ScatterShapeProps) => {
  if (!payload) return null;
  const isHighlighted = payload.isHighlighted ?? payload.isRankedByUser ?? true;
  const imageFilter = isHighlighted ? "none" : "grayscale(1)";
  const imageOpacity = isHighlighted ? 1 : 0.55;
  const labelOffsetY = payload.labelOffsetY ?? 0;
  const labelSide = payload.labelSide ?? "right";
  const textOffsetX = labelSide === "left" ? -(size / 2 + 2) : size / 2 + 2;
  const textAnchor = labelSide === "left" ? "end" : "start";
  const clipId = `scatter-node-${(payload.optionId ?? payload.label ?? "point").replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const labelBaseY = -(size / 2 - 2) + labelOffsetY;
  const labelRotation = labelSide === "left" ? 45 : -45;

  return (
    <g transform={`translate(${cx}, ${cy})`}>
      {payload.imageUrl ? (
        <>
          <defs>
            <clipPath id={clipId}>
              <circle r={size / 2} />
            </clipPath>
          </defs>
          <image
            href={payload.imageUrl}
            x={-(size / 2)}
            y={-(size / 2)}
            width={size}
            height={size}
            clipPath={`url(#${clipId})`}
            style={{ filter: imageFilter, opacity: imageOpacity }}
            preserveAspectRatio="xMidYMid slice"
          />
        </>
      ) : (
        <g>
          <circle
            r={size / 2}
            fill="#e5e7eb"
            stroke={isHighlighted ? "#0f172a" : "#9ca3af"}
            strokeWidth={1.5}
          >
            <title>{payload.label}</title>
          </circle>
          <text x={0} y={5} textAnchor="middle" fontSize={12} fill="#0f172a" fontWeight={700}>
            {payload.label?.charAt(0).toUpperCase() ?? "?"}
          </text>
        </g>
      )}
      <text
        x={textOffsetX}
        y={labelBaseY}
        fontSize={12}
        fill="#0f172a"
        fontWeight={600}
        textAnchor={textAnchor}
        transform={`rotate(${labelRotation} ${textOffsetX} ${labelBaseY})`}
      >
        {payload.label}
      </text>
    </g>
  );
};

export const positionScatterLabels = <T extends BaseScatterPoint>(
  data: T[],
  {
    getX = (point: T) => point.xValue ?? 0,
    getY = (point: T) => point.yValue ?? 0,
    domainMax = 100,
    labelSpacing = 20,
    groupingPrecision,
    alternateGroupPredicate,
  }: ScatterLabelOptions<T> = {}
) => {
  // Determine label side based on x position (right edge gets left-aligned labels)
  const rightEdgeThreshold = domainMax * 0.75;
  
  const withLabelSide = data.map((point) => {
    const x = getX(point);
    const labelSide: "left" | "right" = x > rightEdgeThreshold ? "left" : "right";
    return { ...point, labelSide };
  });

  const grouped = withLabelSide.reduce<Map<string, T[]>>((groups, point) => {
    const yValue = getY(point);
    const keyValue = groupingPrecision ? Math.round(yValue / groupingPrecision) * groupingPrecision : yValue;
    const key = typeof keyValue === "number" ? keyValue.toFixed(6) : String(keyValue);
    const points = groups.get(key) ?? [];
    points.push(point as T);
    groups.set(key, points);
    return groups;
  }, new Map());

  const resolveLabelCollisions = (points: T[]) => {
    const minVerticalGap = labelSpacing;
    const xClosenessThreshold = labelSpacing * 3;
    const placed: Array<{ point: T; labelY: number; x: number; labelSide: "left" | "right" }> = [];

    return points.map((point) => {
      const baseY = getY(point);
      const x = getX(point);
      const labelSide = (point as any).labelSide ?? "right";
      let offsetY = point.labelOffsetY ?? 0;
      let labelY = baseY + offsetY;
      let attempts = 0;

      // Only check collisions with points on the same label side
      while (
        placed.some(
          (placedPoint) =>
            placedPoint.labelSide === labelSide &&
            Math.abs(labelY - placedPoint.labelY) < minVerticalGap &&
            Math.abs(x - placedPoint.x) < xClosenessThreshold
        ) &&
        attempts < 10
      ) {
        const direction = attempts % 2 === 0 ? 1 : -1;
        const magnitude = Math.floor(attempts / 2) + 1;
        offsetY = (point.labelOffsetY ?? 0) + direction * magnitude * labelSpacing;
        labelY = baseY + offsetY;
        attempts++;
      }

      const resolved = { ...point, labelOffsetY: offsetY };
      placed.push({ point: resolved, labelY, x, labelSide });
      return resolved;
    });
  };

  const positioned: T[] = [];

  grouped.forEach((group, key) => {
    const sorted = [...group].sort((a, b) => getX(a) - getX(b));
    const shouldAlternate = alternateGroupPredicate?.(key, sorted) ?? false;
    const xSpan =
      sorted.length > 1 ? getX(sorted[sorted.length - 1]) - getX(sorted[0]) : 0;
    const shouldAlternateSides = sorted.length > 2 && xSpan <= labelSpacing * 2.5;

    if (sorted.length === 1) {
      const [point] = sorted;
      positioned.push({
        ...point,
        labelOffsetY: point.labelOffsetY ?? 0,
      });
      return;
    }

    if (shouldAlternate) {
      sorted.forEach((point, idx) => {
        const direction = idx % 2 === 0 ? -1 : 1;
        const magnitude = Math.floor(idx / 2) + 1;
        positioned.push({
          ...point,
          labelOffsetY: point.labelOffsetY ?? direction * magnitude * labelSpacing,
        });
      });
      return;
    }

    const middle = (sorted.length - 1) / 2;
    sorted.forEach((point, idx) => {
      const offsetIndex = idx - middle;
      positioned.push({
        ...point,
        labelOffsetY: point.labelOffsetY ?? offsetIndex * labelSpacing,
      });
    });
  });

  const withCollisionResolution = resolveLabelCollisions(positioned);

  return withCollisionResolution;
};

export const usePositionedScatterData = <T extends BaseScatterPoint>(
  data: T[],
  options: ScatterLabelOptions<T>
) => {
  return useMemo(() => positionScatterLabels(data, options), [data, options]);
};
